'use client'

import { cn } from '@brew/ui/cn'
import { Loader2 } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, ViewTransition } from 'react'
import { ScaledEmailHtmlFrame } from '@/components/email-preview'
import { preloadEmailJsxPreview } from '@/components/email-preview/email-jsx-preview-dynamic'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import {
  TemplateOpenCount,
  TemplateRemixCount,
} from '@/components/templates/template-metadata'
import { capitalize } from '@/lib/shared/text/capitalize'
import { publicTemplateReferenceOf } from '@/lib/templates/public-reference'
import { formatBrandNameFromDomain } from '../lib/brand-name'
import {
  TEMPLATE_CARD_CLASSES,
  TEMPLATE_CARD_IMAGE_SIZES,
  TEMPLATE_CARD_PREVIEW_CLASSES,
} from '../lib/grid-classes'
import type { TemplateBrandMetaByDomain, TemplateSnapshot } from '../lib/types'
import { TemplateBrandLogo } from './template-brand-logo'

const BLUR_PLACEHOLDER =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjE3OCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PC9zdmc+'

const WORD_SEPARATOR = /[-\s]+/

/**
 * Title-case a hyphen / space-separated value for use in the hover
 * badge. Used for the row's `category` (e.g., `welcome` -> `Welcome`,
 * `password-reset` -> `Password Reset`). NEVER fed any per-row
 * type field — the `/templates` gallery is intentionally
 * type-agnostic and surfaces both campaign and automation rows with
 * NO indication of the original type on the card. See
 * `tests/templates-gallery-mixes-types.test.ts` for the regression
 * guard.
 */
function formatBadgeLabel(value: string): string {
  return value.split(WORD_SEPARATOR).map(capitalize).join(' ')
}

interface EmailPreviewCardProps {
  snapshot: TemplateSnapshot
  /** Curated brand display meta (name + rehosted logo) keyed by domain. */
  brandMeta?: TemplateBrandMetaByDomain
  href: string
}

function preloadDetailPreview() {
  preloadEmailJsxPreview().catch(() => {
    // The detail page retries the import when it renders.
  })
}

// ----------------------------------------------------------------------------
// PREVIEW VARIANTS
// ----------------------------------------------------------------------------

function ProcessingPreview({ title }: { title: string }) {
  const t = useTranslations('templates')
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
      <div className="text-center">
        <p className="font-medium text-foreground/70 text-xs">
          {t('card.converting')}
        </p>
        <p className="mt-0.5 line-clamp-2 max-w-[140px] text-caption text-muted-foreground leading-tight">
          {title}
        </p>
      </div>
    </div>
  )
}

function CardImage({
  emailId,
  imagePngUrl,
  title,
}: {
  emailId: string
  imagePngUrl: string
  /** Used as the `<img alt>` text — must not leak any per-row type. */
  title: string
}) {
  const t = useTranslations('templates')
  return (
    <ViewTransition name={`email-image-${emailId}`}>
      <Image
        key={`email-image-${emailId}`}
        id={`email-image-${emailId}`}
        src={imagePngUrl}
        alt={t('card.imageAlt', { title })}
        fill
        sizes={TEMPLATE_CARD_IMAGE_SIZES}
        quality={75}
        className="object-cover object-top"
        loading="lazy"
        placeholder="blur"
        blurDataURL={BLUR_PLACEHOLDER}
      />
    </ViewTransition>
  )
}

/**
 * Render the raw `emailHtml` scaled to the card width and trigger a one-shot
 * screenshot job so the next view can use the cheaper `<Image />` path.
 *
 * `ScaledEmailHtmlFrame` pins the document to its own design width (parsed
 * from the HTML; 600 fallback) and downscales it to the card — the same
 * hardened, same-origin frame every other read-only email surface uses, so
 * a fixed 600 never squeezes wider (640/680) templates. The card's 9:16 box
 * clips whatever runs below the fold.
 */
function CardIframe({
  emailId,
  emailHtml,
}: {
  emailId: string
  emailHtml: string
}) {
  const t = useTranslations('templates')
  const screenshotTriggered = useRef(false)

  useEffect(() => {
    if (screenshotTriggered.current) {
      return
    }
    screenshotTriggered.current = true

    fetch('/api/emails/screenshot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailId }),
    }).catch(() => {})
  }, [emailId])

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-white">
      <ScaledEmailHtmlFrame html={emailHtml} title={t('card.previewTitle')} />
    </div>
  )
}

function MissingPreview() {
  const t = useTranslations('templates')
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-muted/20">
      <span className="text-muted-foreground/40 text-xs">
        {t('card.noPreview')}
      </span>
    </div>
  )
}

function CardPreview({
  snapshot,
  isProcessing,
}: {
  snapshot: TemplateSnapshot
  isProcessing: boolean
}) {
  const publicReference = publicTemplateReferenceOf(snapshot)
  if (isProcessing) {
    return <ProcessingPreview title={snapshot.title} />
  }
  if (snapshot.imagePngUrl) {
    return (
      <CardImage
        emailId={publicReference}
        imagePngUrl={snapshot.imagePngUrl}
        title={snapshot.title}
      />
    )
  }
  if (snapshot.emailHtml) {
    return (
      <CardIframe emailId={snapshot.emailId} emailHtml={snapshot.emailHtml} />
    )
  }
  return <MissingPreview />
}

// ----------------------------------------------------------------------------
// CARD
// ----------------------------------------------------------------------------

/**
 * Metadata footer below the artwork (App Store style): brand logo, then
 * a two-line text block — brand name over the email title — with the
 * remix metric (icon + count, no words) across from it when the design
 * has earned one (`remixCount > 0` — fresh rows carry no counter).
 * Falls back to the title as the lead line when the row predates
 * `brandDomain`.
 */
function CardMeta({
  snapshot,
  brandMeta,
}: {
  snapshot: TemplateSnapshot
  brandMeta?: TemplateBrandMetaByDomain
}) {
  // Curated display meta wins; domains outside the map (forwarded rows,
  // a brand published mid-cache) keep the derived name + hotlink cascade.
  const curated = snapshot.brandDomain
    ? brandMeta?.[snapshot.brandDomain]
    : undefined
  const brandName = snapshot.brandDomain
    ? (curated?.name ?? formatBrandNameFromDomain(snapshot.brandDomain))
    : null
  const remixCount = snapshot.remixCount ?? 0
  const openedCount = snapshot.openedCount

  return (
    <div className="mt-3 flex w-full min-w-0 items-center gap-2.5">
      {snapshot.brandDomain && brandName ? (
        <TemplateBrandLogo
          domain={snapshot.brandDomain}
          name={brandName}
          logoUrl={curated?.logoUrl}
          className="size-7 shrink-0"
        />
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-foreground text-sm">
          {brandName ?? snapshot.title}
        </span>
        {brandName ? (
          <span className="truncate text-muted-foreground text-xs">
            {snapshot.title}
          </span>
        ) : null}
      </span>
      {openedCount === undefined ? (
        <TemplateRemixCount remixCount={remixCount} />
      ) : (
        <TemplateOpenCount openedCount={openedCount} />
      )}
    </div>
  )
}

export function EmailPreviewCard({
  snapshot,
  brandMeta,
  href,
}: EmailPreviewCardProps) {
  const t = useTranslations('templates')
  const isProcessing =
    snapshot.status === 'streaming' || snapshot.status === 'error'
  const publicReference = publicTemplateReferenceOf(snapshot)
  const cardProps = {
    id: `email-card-${publicReference}`,
    className: cn(TEMPLATE_CARD_CLASSES, 'template-card-item', {
      'cursor-pointer': !isProcessing,
      'cursor-default': isProcessing,
    }),
  }
  const content = (
    <>
      {/* Hover affordances scope to the artwork box only — the scrim
          must never wash over the metadata footer below. */}
      <div
        className={cn(
          TEMPLATE_CARD_PREVIEW_CLASSES,
          'transition-[scale,box-shadow] duration-fast ease-smooth-out',
          {
            'group-hover:scale-[1.015] group-hover:shadow-xl': !isProcessing,
          }
        )}
      >
        <CardPreview snapshot={snapshot} isProcessing={isProcessing} />

        {!isProcessing ? (
          /*
            Hover badge: surfaces the row's `category` (welcome,
            newsletter, password-reset, ...) — NEVER any per-row type.
            The gallery shows both campaign- and automation-typed rows
            without indicating which is which, so the badge stays
            information-rich (category is what users actually care
            about) while staying type-agnostic. Falls back to "Email"
            when no `category` is set. There is deliberately NO hover
            "Remix" overlay — the whole card is the click target and the
            artwork stays unobstructed (Apple-gallery restraint).
          */
          <div className="absolute top-3 right-3 z-10 rounded-lg bg-background px-2.5 py-1 font-medium text-foreground text-xs opacity-0 transition-opacity duration-quick group-hover:opacity-100">
            {snapshot.category
              ? formatBadgeLabel(snapshot.category)
              : t('card.categoryFallback')}
          </div>
        ) : null}
      </div>

      <CardMeta snapshot={snapshot} brandMeta={brandMeta} />
    </>
  )

  return isProcessing ? (
    <button {...cardProps} type="button" disabled>
      {content}
    </button>
  ) : (
    <IntentPrefetchLink
      {...cardProps}
      href={href}
      onPointerEnter={preloadDetailPreview}
      onFocus={preloadDetailPreview}
    >
      {content}
    </IntentPrefetchLink>
  )
}
